import moment from 'moment';

// Colour per line tone. Values match the pre-consolidation constants
// (antd green-7 / red-6 / orange-6 / grey), aligned with STATE_META in
// OptimiserRunIcon.jsx.
export const TONE_COLOURS = {
  ok: '#389e0d',
  error: '#cf1322',
  activity: '#fa8c16',
  cancelled: '#722ed1',
  idle: '#bfbfbf',
  unknown: '#bfbfbf'
};

// A `finished` run reverts to `idle` once it falls outside its "recently
// finished" window, so the badge doesn't keep showing a stale "Finished"
// state indefinitely. (Moved verbatim from index.jsx, which re-exports it —
// views/OptimiserStatus/page.jsx imports it from there.)
export const effectiveRunState = (runStatus) => {
  if (!runStatus) {
    return 'unknown';
  }
  const state = runStatus.state || 'unknown';
  if (state === 'finished' || state === 'failed' || state === 'cancelled') {
    const newest = (runStatus.recentTerminalRuns || [])[0];
    if (newest && newest.finishedAt) {
      const windowMs = (runStatus.finishedWindowSeconds || 600) * 1000;
      if (Date.now() - new Date(newest.finishedAt).getTime() > windowMs) {
        return 'idle';
      }
    }
  }
  return state;
};

const RUN_STATE_LABELS = {
  running: 'Running',
  queued: 'Queued',
  idle: 'Idle',
  finished: 'Finished',
  failed: 'Failed',
  cancelled: 'Cancelled',
  unknown: 'Unknown'
};

const RUN_STATE_TONES = {
  running: 'activity',
  queued: 'activity',
  idle: 'idle',
  finished: 'ok',
  failed: 'error',
  cancelled: 'cancelled',
  unknown: 'unknown'
};

// "(1 running, 2 queued)" — zero-count classes omitted; empty when nothing active.
const activeCountsSuffix = (activeRuns) => {
  const runningCount = activeRuns.filter(r => r.state === 'running').length;
  const queuedCount = activeRuns.filter(r => r.state === 'queued').length;
  const parts = [];
  if (runningCount > 0) {
    parts.push(`${runningCount} running`);
  }
  if (queuedCount > 0) {
    parts.push(`${queuedCount} queued`);
  }
  return parts.length > 0 ? ` (${parts.join(', ')})` : '';
};

const runLine = (runState, activeRuns, drivingRun) => {
  const label = RUN_STATE_LABELS[runState] || RUN_STATE_LABELS.unknown;
  const at = drivingRun && drivingRun.receivedAt;
  const when = at ? ` · ${moment(at).format('DD MMM YYYY HH:mm')}` : '';
  return {
    key: 'run',
    text: `Optimiser: ${label}${activeCountsSuffix(activeRuns)}${when}`,
    tone: RUN_STATE_TONES[runState] || 'unknown'
  };
};

// One sub-line per active run for the popover (tooltip caps at 3 — see index.jsx).
const buildRunLines = activeRuns => activeRuns.map(run => ({
  key: `run-${run.runIdentifier}`,
  text: `${run.runName || run.runIdentifier}`
    + ` · ${RUN_STATE_LABELS[run.state] || RUN_STATE_LABELS.unknown}`
    + `${run.triggeredAt ? ` · ${moment(run.triggeredAt).format('DD MMM YYYY HH:mm')}` : ''}`,
  tone: RUN_STATE_TONES[run.state] || 'unknown'
}));

const matchesAcknowledged = (acknowledgedRuns, run) => acknowledgedRuns.some(
  a => a.runIdentifier === run.runIdentifier && a.receivedAt === run.receivedAt
);

const validationLine = (count) => {
  if (count > 0) {
    return {
      key: 'validation',
      text: `${count} outstanding validation failure${count === 1 ? '' : 's'}`,
      tone: 'error'
    };
  }
  return { key: 'validation', text: 'No outstanding validation failures', tone: 'ok' };
};

const BROKER_TEXTS = {
  healthy: 'Broker comms: Healthy',
  unhealthy: 'Broker comms: failures detected'
};

const BROKER_TONES = { healthy: 'ok', unhealthy: 'error' };

const brokerLine = (brokerHealth) => {
  const state = brokerHealth ? brokerHealth.state : undefined;
  return {
    key: 'broker',
    text: BROKER_TEXTS[state] || 'Broker comms: status unknown',
    tone: BROKER_TONES[state] || 'unknown'
  };
};

// Single source of truth for everything the badge displays: the alert corner
// (alert/flashing/count), the combined tooltip and the popover status section
// (lines + runLines) all render from this one object, so they can never disagree.
export const summariseOptimiserStatus = ({
  runStatus, count, brokerHealth, acknowledgedRuns = []
}) => {
  const acks = Array.isArray(acknowledgedRuns) ? acknowledgedRuns : [];
  const activeRuns = (runStatus && runStatus.activeRuns) || [];
  const terminalRuns = (runStatus && runStatus.recentTerminalRuns) || [];
  let runState = effectiveRunState(runStatus);
  let drivingRun = activeRuns[0] || null;
  if (runState === 'finished' || runState === 'failed' || runState === 'cancelled') {
    // The newest UNACKNOWLEDGED terminal run drives a terminal badge; only when
    // every recent terminal run is acknowledged does the badge revert to idle.
    const unacknowledged = terminalRuns.filter(r => !matchesAcknowledged(acks, r));
    if (unacknowledged.length === 0) {
      runState = 'idle';
      drivingRun = null;
    }
    else {
      runState = unacknowledged[0].state;
      [drivingRun] = unacknowledged;
    }
  }
  const failures = count || 0;
  const brokerUnhealthy = Boolean(brokerHealth && brokerHealth.state === 'unhealthy');
  let alert = 'none';
  if (failures > 0) {
    alert = 'count';
  }
  else if (brokerUnhealthy) {
    alert = 'dot';
  }
  return {
    runState,
    alert,
    flashing: brokerUnhealthy,
    count: failures,
    lines: [runLine(runState, activeRuns, drivingRun), validationLine(failures), brokerLine(brokerHealth)],
    runLines: buildRunLines(activeRuns)
  };
};
